import Flex from 'legacyfrontend/components/Flex';
import paquetesApi from 'legacyfrontend/api/paquetes';
import { FALLBACK_IMAGE, DATE_KEYS } from 'legacyfrontend/constants';
import { usePackageCard } from 'legacyfrontend/contexts/PackageCardContext';
import getFinalPrice from 'legacyfrontend/utils/get-final-price';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import { formatWithMomentFormat, utcToLocal } from 'legacyfrontend/utils/dateUtils';
import useCurrentLanguage from 'legacyfrontend/hooks/useCurrentLanguage';
import CardGallery from './components/CardGallery';
import DynamicContent from './components/DynamicContent';
import DynamicIncludesRow from './DynamicIncludesRow';
import totalNightTextSetter from 'legacyfrontend/utils/total-night-text';
import { selectApplicationState, selectToken } from 'legacyfrontend/redux/selectors/application-selectors';
import { useSelector } from 'react-redux';
import { MenuItem, Popper, Paper, ClickAwayListener } from '@mui/material';

const PackageCard = () => {
  const { t } = useTranslation(['layout']);
  const urlParams = new URLSearchParams(isClient ? window.location.search : '');
  const currentLanguage = useCurrentLanguage();
  const [showGallery, setShowGallery] = useState();
  const [availableDates, setAvailableDates] = useState([]);
  const [dateSince, setDateSince] = useState(null);
  const { packageData, displayInfo, handleDisplayInfoToggle } = usePackageCard();
  const { web } = useSelector(selectApplicationState);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const token = useSelector(selectToken);

  const isInternal = useMemo(() => packageData?.isInternal, [packageData]);
  const url = web === 'localhost' ? `http://localhost:${process.env.PORT}` : `https://${web}`;

  const selectedMonth = urlParams?.get('monthYear') ? parseInt(urlParams.get('monthYear').split('-')[0]) : null;
  const selectedYear = urlParams?.get('monthYear') ? parseInt(urlParams.get('monthYear').split('-')[1]) : null;

  const monthYear = useMemo(() => {
    if (!selectedMonth || !selectedYear) return null;
    return `${selectedMonth.toString().padStart(2, '0')}-${selectedYear}`;
  }, [selectedMonth, selectedYear]);

  const handlePackageSalidas = useCallback(async () => {
    if (!monthYear || !packageData?.externalId || !token) return;
    
    try {
      const response = await paquetesApi.getInternalPackageSalidas(packageData.externalId, monthYear, token);
      setAvailableDates(response);
    } catch (error) {
      console.error(`Error al obtener las salidas: ${error?.response?.data}`);
    }
  }, [packageData?.externalId, monthYear, token]);

  const {
    title,
    images,
    totalNights,
    packageOrigins,
    packageDestinations,
    currencyCode,
    cheapestOption,
  } = packageData;

  const departureDate = useMemo(() => {
    const tripInformation = cheapestOption?.flight?.tripInformation;
    if (tripInformation && tripInformation.length > 0) {
      const sortedTripInfo = [...tripInformation].sort((a, b) => a.id - b.id);
      const flightDepartureDate = sortedTripInfo[0]?.departureDate;
      if (flightDepartureDate) {
        const dateFormats = {
          es: 'D [de] MMMM [de] YYYY',
          en: 'MMMM D, YYYY',
          pt: 'D [de] MMMM [de] YYYY',
        };

        const format = dateFormats[currentLanguage] || dateFormats.es;
        return formatWithMomentFormat(utcToLocal(flightDepartureDate), format);
      }
    }
    return null;
  }, [cheapestOption, currentLanguage]);

  const galleyOfImages = useMemo(() => {
    if (images && images.length > 0) {
      return images;
    }

    if (packageDestinations && packageDestinations.length > 0) {
      const destinationImages = packageDestinations
        .map((dest) => dest.destinationCity?.imageUrl)
        .filter((imageUrl) => imageUrl && imageUrl.trim() !== '');

      if (destinationImages.length > 0) {
        return destinationImages;
      }
    }

    return [FALLBACK_IMAGE];
  }, [images, packageDestinations]);

  const mainImage = useMemo(() => {
    if (images && images.length > 0) {
      return images[0];
    }

    if (packageDestinations && packageDestinations.length > 0) {
      const firstDestinationImage = packageDestinations[0]?.destinationCity?.imageUrl;
      if (firstDestinationImage && firstDestinationImage.trim() !== '') {
        return firstDestinationImage;
      }
    }

    return FALLBACK_IMAGE;
  }, [images, packageDestinations]);

  const handleShowGallery = () => {
    setShowGallery((state) => !state);
  };

  const formatSalidaLabel = useCallback((date) => {
    const sinceDate = new Date(date.dateSince);
    const untilDate = new Date(date.dateUntil);
    
    const sinceUTC = new Date(sinceDate.getUTCFullYear(), sinceDate.getUTCMonth(), sinceDate.getUTCDate());
    const untilUTC = new Date(untilDate.getUTCFullYear(), untilDate.getUTCMonth(), untilDate.getUTCDate());
    
    const dateSince = formatWithMomentFormat(sinceUTC, DATE_KEYS.FORMAT.DAY_SHORT);
    const dateUntil = formatWithMomentFormat(untilUTC, DATE_KEYS.FORMAT.DAY_SHORT);

    if (dateSince === dateUntil) {
      return `Salida: ${dateSince}`;
    } else {
      return `Salida: ${dateSince} al ${dateUntil}`;
    }
  }, []);

  const handleClick = useCallback((salidaId) => {
    if (isClient) {
      const urlSalidas = `${url}/paquetes?id=${packageData.externalId}&salidaId=${salidaId}`;
      window.open(urlSalidas, '_blank');
    }
  }, [url, packageData.externalId]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (isInternal) {
      handlePackageSalidas();
    }
  }, [isInternal, handlePackageSalidas]);

  // Setear la primera fecha disponible cuando se cargan las fechas
  useEffect(() => {
    if (availableDates.length > 0 && !dateSince) {
      const firstDate = availableDates[0];
      const sinceDate = new Date(firstDate.dateSince);
      const sinceUTC = new Date(sinceDate.getUTCFullYear(), sinceDate.getUTCMonth(), sinceDate.getUTCDate());
      const formattedDate = formatWithMomentFormat(sinceUTC, DATE_KEYS.FORMAT.NUMBER_MONTHNAME_YEAR);
      setDateSince(formattedDate);
    }
  }, [availableDates, dateSince]);

  return (
    <Flex vertical className='package-card'>
      <Flex className='package-card__data'>
        <img
          className='package-card__data__img'
          onClick={handleShowGallery}
          src={mainImage}
          alt='Package Destination'
        />
        <Flex vertical className='package-card__data__wrapper'>
          <Flex vertical className='package-card__data__wrapper__info'>
            <h3 className='package-card__data__wrapper__info__title'>{title}</h3>
            <div className='package-card__data__wrapper__info__details'>
              <p>
                <strong>{t('packages.details.departure')}:</strong>
                <span>{packageOrigins[0].originExporta.nombreCiudad}</span>
              </p>
              <p>
                <strong>{t('searcher.destination')}:</strong>
                <span>{packageDestinations[0].destinationCity.nombreCiudad}</span>
              </p>
              <p>
                <strong>{totalNightTextSetter(totalNights)}</strong>
              </p>
              {(dateSince || departureDate) && (
                <p className='package-card__data__wrapper__info__details__departure'>
                  <strong>Salidas desde:</strong>
                  <span>{dateSince ?? departureDate}</span>
                </p>
              )}
              <p className='package-card__data__wrapper__info__details__price'>
                <strong>{t('common.since')}:</strong>
                <span>
                  {getFinalPrice({
                    currencyCode,
                    total: cheapestOption.totalManager.total,
                  })}
                </span>
              </p>
            </div>
          </Flex>
          <Flex className='package-card__data__wrapper__footer'>
            {isInternal ? (
              <DynamicIncludesRow paquetesIncluidos={packageData?.icons} fullWidth />
            ) : (
              <DynamicIncludesRow paquetesIncluidos={[]} fullWidth />
            )}
            <Flex className='package-card__data__wrapper__footer__buttons'>
              {!isInternal && (
                <button
                  className={displayInfo ? 'active' : undefined}
                  onClick={handleDisplayInfoToggle}
                >
                  {t('common.more_details')}
                </button>
              )}
              {isInternal && (
                <>
                  <button
                    id="basic-button"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleOpen}
                  >
                    Ver Salidas
                  </button>
                  <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    modifiers={[
                      { name: 'flip', enabled: false },
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 4],
                        },
                      },
                    ]}
                    style={{ zIndex: 1300 }}
                  >
                    <ClickAwayListener onClickAway={handleClose}>
                      <Paper elevation={6} sx={{ boxShadow: 6 }}>
                        {availableDates?.map((date) => (
                          <MenuItem key={date.salidaId} onClick={() => {
                            handleClick(date.salidaId);
                            handleClose();
                          }}>
                            {formatSalidaLabel(date)}
                          </MenuItem>
                        ))}
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <DynamicContent />
      <CardGallery
        showGallery={showGallery}
        handleShowGallery={handleShowGallery}
        packageDestinations={packageDestinations}
        images={galleyOfImages}
      />
    </Flex>
  );
};

export default PackageCard;
